import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PlatformService } from '@/utils/platform';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useRecordingSessionStore, RecordingSession } from '@/stores/recordingSessionStore';
import { useOrganizationFeatureFlags } from '@/stores/organizationFeatureFlagsStore';
import { RecordingRecoveryModal } from '@/components/recording/RecordingRecoveryModal';
import { UploadJobController } from '@/components/recording/UploadJobController';
import Landing from '@/pages/Landing';
import Features from '@/pages/Features';
import About from '@/pages/About';
import Pricing from '@/pages/Pricing';
import Contact from '@/pages/Contact';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import EULA from '@/pages/EULA';
import Login from '@/pages/Login';
import MobileLogin from '@/pages/MobileLogin';
// Register page disabled - organizations created through Platform Management
// import Register from '@/pages/Register';
import AcceptInvitation from '@/pages/AcceptInvitation';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Appointments from '@/pages/Appointments';
import CreateAppointment from '@/pages/CreateAppointment';
import Patients from '@/pages/Patients';
import CreatePatient from '@/pages/CreatePatient';
import EditPatient from '@/pages/EditPatient';
import { PatientCockpit } from '@/components/cockpit';
import Recording from '@/pages/Recording';
import QuickRecording from '@/pages/QuickRecording';
import { Settings } from '@/pages/Settings';
import { Templates } from '@/pages/Templates';
import { ClinicalValidation } from '@/pages/ClinicalValidation';
import Profile from '@/pages/Profile';
import EncounterList from '@/pages/EncounterList';
import ManagementDashboard from '@/pages/ManagementDashboard';
import OrganizationsList from '@/pages/OrganizationsList';
import PlatformUsers from '@/pages/PlatformUsers';
import PlatformSettings from '@/pages/PlatformSettings';
import Operations from '@/pages/Operations';
import AICostDashboard from '@/pages/AICostDashboard';
import ExternalIntegrationsDashboard from '@/pages/ExternalIntegrationsDashboard';
import CoverageAudit from '@/pages/CoverageAudit';
import CoverageAuditEncounterDetail from '@/pages/CoverageAuditEncounterDetail';
import CoverageAuditStageDashboard from '@/pages/CoverageAuditStageDashboard';
import CoverageAuditModelInspector from '@/pages/CoverageAuditModelInspector';
import SubscriptionPlansPage from '@/pages/SubscriptionPlansPage';
import PlatformBillingPage from '@/pages/PlatformBillingPage';
import ComplianceDashboard from '@/pages/ComplianceDashboard';
import OrganizationUsageDashboard from '@/pages/OrganizationUsageDashboard';
import MyUsagePage from '@/pages/MyUsagePage';
import PartnerOrganizations from '@/pages/PartnerOrganizations';
import PartnerUsers from '@/pages/PartnerUsers';
import PlatformPartners from '@/pages/PlatformPartners';
import PlatformEnterprises from '@/pages/PlatformEnterprises';
import EnterpriseDashboard from '@/pages/EnterpriseDashboard';
import EnterpriseChannelPartners from '@/pages/EnterpriseChannelPartners';
import Layout from '@/components/Layout';
import ConsoleUsageReport from '@/components/usage/ConsoleUsageReport';
import ManagementLayout from '@/components/ManagementLayout';
import PartnerLayout from '@/components/PartnerLayout';
import EnterpriseLayout from '@/components/EnterpriseLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import LoadingSpinner from '@/components/LoadingSpinner';
import EncounterRedirect from '@/components/EncounterRedirect';
import AppointmentDetails from '@/pages/AppointmentDetails';

// Roles that have access to the Platform (Management Console)
const PLATFORM_ROLES = ['superadmin', 'enterprise_admin', 'channel_partner_admin', 'partner_admin'];

// Helper component for role-based home redirect
const RoleBasedHome = () => {
  const { user } = useAuth();

  // Platform roles go to Management Console
  // - superadmin: full platform control
  // - enterprise_admin: manage their channel partners
  // - channel_partner_admin/partner_admin: manage their organizations
  if (PLATFORM_ROLES.includes(user?.role || '')) {
    return <Navigate to="/management" replace />;
  }

  // Enterprise users (read-only) go to Enterprise Portal
  if (user?.role === 'enterprise_user') {
    return <Navigate to="/enterprise" replace />;
  }

  // Channel Partner users (read-only) go to Partner Portal
  if (['partner_user', 'channel_partner_user'].includes(user?.role || '')) {
    return <Navigate to="/partner" replace />;
  }

  // Clinical roles (admin, operator, staff) go to clinical dashboard
  return <Navigate to="/dashboard" replace />;
};

// Guard: Platform roles can access management routes
// - superadmin: full access
// - enterprise_admin: scoped to their enterprise
// - channel_partner_admin/partner_admin: scoped to their partner
const PlatformRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  if (!PLATFORM_ROLES.includes(user?.role || '')) {
    // Non-platform roles are redirected to clinical dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Guard: Management Console pages that require a specific platform permission.
// Used for superadmin-only sections (global settings, subscriptions/billing,
// compliance) so non-superadmin platform roles (enterprise_admin) cannot open a
// page whose API calls would 403. Layers on top of PlatformRoute.
const PermissionRoute = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const { user, hasPermission } = useAuth();

  if (!PLATFORM_ROLES.includes(user?.role || '')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!hasPermission(permission)) {
    // Platform role without the required permission → back to the console home.
    return <Navigate to="/management" replace />;
  }

  return <>{children}</>;
};

// Guard: Only clinical roles can access clinical routes
// Platform roles (superadmin, enterprise_admin, channel_partner_admin) cannot access clinical data
const ClinicalRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  // Platform roles go to Management Console - they cannot access clinical data (HIPAA)
  if (PLATFORM_ROLES.includes(user?.role || '')) {
    return <Navigate to="/management" replace />;
  }

  // Enterprise users (read-only) go to Enterprise Portal - cannot access clinical data
  if (user?.role === 'enterprise_user') {
    return <Navigate to="/enterprise" replace />;
  }

  // Partner users (read-only) go to Partner Portal - cannot access clinical data
  if (['partner_user', 'channel_partner_user'].includes(user?.role || '')) {
    return <Navigate to="/partner" replace />;
  }

  // Clinical roles (admin, operator, staff) can access clinical routes
  return <>{children}</>;
};

// Guard: Only enterprise_user can access enterprise portal (read-only view)
// enterprise_admin now goes to Platform Management Console
const EnterpriseRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  // enterprise_admin goes to Platform, not Enterprise Portal
  if (user?.role === 'enterprise_admin') {
    return <Navigate to="/management" replace />;
  }

  // Only enterprise_user can access Enterprise Portal
  if (user?.role !== 'enterprise_user') {
    // Platform roles go to Management
    if (PLATFORM_ROLES.includes(user?.role || '')) {
      return <Navigate to="/management" replace />;
    }
    // Partner users go to Partner Portal
    if (['partner_user', 'channel_partner_user'].includes(user?.role || '')) {
      return <Navigate to="/partner" replace />;
    }
    // Clinical roles go to Dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Guard: Only partner_user/channel_partner_user can access partner portal (read-only view)
// partner_admin/channel_partner_admin now go to Platform Management Console
const PartnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  // Partner admins go to Platform, not Partner Portal
  if (['partner_admin', 'channel_partner_admin'].includes(user?.role || '')) {
    return <Navigate to="/management" replace />;
  }

  // Only partner_user/channel_partner_user can access Partner Portal
  if (!['partner_user', 'channel_partner_user'].includes(user?.role || '')) {
    // Platform roles go to Management
    if (PLATFORM_ROLES.includes(user?.role || '')) {
      return <Navigate to="/management" replace />;
    }
    // Enterprise user goes to Enterprise Portal
    if (user?.role === 'enterprise_user') {
      return <Navigate to="/enterprise" replace />;
    }
    // Clinical roles go to Dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Component to handle interrupted recording recovery
const RecordingRecoveryHandler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { interruptedSessions, clearInterruptedSession, clearAllInterruptedSessions } = useRecordingSessionStore();
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [currentSession, setCurrentSession] = useState<RecordingSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for interrupted sessions when user is authenticated
  useEffect(() => {
    if (user && interruptedSessions.length > 0) {
      // Show the most recent interrupted session
      const mostRecent = interruptedSessions.reduce((latest, session) =>
        session.lastActiveAt > latest.lastActiveAt ? session : latest
      );
      setCurrentSession(mostRecent);
      setShowRecoveryModal(true);
    }
  }, [user, interruptedSessions]);

  const handleResume = () => {
    if (!currentSession) return;

    setIsLoading(true);

    // Navigate to the recording page with recovery context
    // The Recording page will handle the actual recovery
    const params = new URLSearchParams({
      recover: 'true',
      sessionId: currentSession.id,
    });

    if (currentSession.appointmentId) {
      navigate(`/recording/${currentSession.appointmentId}?${params.toString()}`);
    } else if (currentSession.encounterId) {
      navigate(`/recording?encounterId=${currentSession.encounterId}&${params.toString()}`);
    } else {
      navigate(`/recording?${params.toString()}`);
    }

    setShowRecoveryModal(false);
    setIsLoading(false);
  };

  const handleDiscard = () => {
    if (!currentSession) return;

    setIsLoading(true);
    clearInterruptedSession(currentSession.id);

    // Check if there are more sessions to show
    const remaining = interruptedSessions.filter(s => s.id !== currentSession.id);
    if (remaining.length > 0) {
      const nextSession = remaining.reduce((latest, session) =>
        session.lastActiveAt > latest.lastActiveAt ? session : latest
      );
      setCurrentSession(nextSession);
    } else {
      setShowRecoveryModal(false);
      setCurrentSession(null);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    // Just close the modal without discarding - user can access later from dashboard
    setShowRecoveryModal(false);
  };

  if (!showRecoveryModal || !currentSession) return null;

  return (
    <RecordingRecoveryModal
      isOpen={showRecoveryModal}
      session={currentSession}
      onResume={handleResume}
      onDiscard={handleDiscard}
      onClose={handleClose}
      isLoading={isLoading}
    />
  );
};

function App() {
  const { initialize, isLoading, isInitialized, user } = useAuth();
  const isNative = PlatformService.isNative();
  const fetchFeatureFlags = useOrganizationFeatureFlags((s) => s.fetchFlags);
  const resetFeatureFlags = useOrganizationFeatureFlags((s) => s.reset);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Load organization feature flags once the user is authenticated AND has an
  // organization context. Superadmins without an organizationId skip the
  // fetch entirely — they have no org-scoped flags to read. Depends on the
  // specific user id and organizationId so switching orgs forces a refetch.
  useEffect(() => {
    if (user?.organizationId) {
      fetchFeatureFlags();
    } else {
      resetFeatureFlags();
    }
  }, [user?.id, user?.organizationId, fetchFeatureFlags, resetFeatureFlags]);

  // Configure StatusBar and Safe Area for native apps
  useEffect(() => {
    if (isNative) {
      // Add 'capacitor' class to body for CSS conditional safe area
      document.body.classList.add('capacitor');

      // Set status bar overlay mode to work with safe area
      StatusBar.setOverlaysWebView({ overlay: true }).catch(console.error);

      // Set default style (will be overridden by specific pages if needed)
      StatusBar.setStyle({ style: Style.Dark }).catch(console.error);

      // Android-specific: set background color with transparency
      if (PlatformService.isAndroid()) {
        StatusBar.setBackgroundColor({ color: '#00000000' }).catch(console.error);
      }
    } else {
      // Ensure 'capacitor' class is removed on web
      document.body.classList.remove('capacitor');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('capacitor');
    };
  }, [isNative]);

  if (!isInitialized || isLoading) {
    return <LoadingSpinner />;
  }

  // Determine which login screen to use based on platform
  const loginDestination = isNative ? '/mobile-login' : '/login';
  const LoginComponent = isNative ? MobileLogin : Login;

  return (
    <>
      <Routes>
        {/* Public routes — marketing pages temporarily redirect to login */}
        <Route
          path="/"
          element={
            user
              ? <RoleBasedHome />
              : <Navigate to={loginDestination} replace />
          }
        />
        <Route path="/features" element={<Navigate to="/login" replace />} />
        <Route path="/about" element={<Navigate to="/login" replace />} />
        <Route path="/pricing" element={<Navigate to="/login" replace />} />
        <Route path="/contact" element={<Navigate to="/login" replace />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/eula" element={<EULA />} />

        {/* Native mobile login */}
        <Route
          path="/mobile-login"
          element={user ? <RoleBasedHome /> : <MobileLogin />}
        />

        {/* Web login */}
        <Route
          path="/login"
          element={user ? <RoleBasedHome /> : <Login />}
        />

        {/* Registration disabled - redirect to login */}
        <Route
          path="/register"
          element={<Navigate to="/login" replace />}
        />
        <Route
          path="/accept-invitation"
          element={user ? <RoleBasedHome /> : <AcceptInvitation />}
        />
        <Route
          path="/forgot-password"
          element={user ? <RoleBasedHome /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password/:token"
          element={user ? <RoleBasedHome /> : <ResetPassword />}
        />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* Management Console (Superadmin Only) - Purple Theme */}
          <Route element={<ManagementLayout />}>
            <Route path="/management" element={<PlatformRoute><ManagementDashboard /></PlatformRoute>} />
            <Route path="/management/enterprises" element={<PermissionRoute permission="platform.enterprises.view"><PlatformEnterprises /></PermissionRoute>} />
            <Route path="/management/partners" element={<PermissionRoute permission="platform.partners.view"><PlatformPartners /></PermissionRoute>} />
            <Route path="/management/organizations" element={<PlatformRoute><OrganizationsList /></PlatformRoute>} />
            <Route path="/management/users" element={<PermissionRoute permission="platform.users.view"><PlatformUsers /></PermissionRoute>} />
            <Route path="/management/usage" element={<PermissionRoute permission="platform.usage.report"><ConsoleUsageReport /></PermissionRoute>} />
            <Route path="/management/settings" element={<PermissionRoute permission="platform.system.configure"><PlatformSettings /></PermissionRoute>} />
            <Route path="/management/operations" element={<PermissionRoute permission="platform.triage.view"><Operations /></PermissionRoute>} />
            <Route path="/management/operations/user/:id" element={<PermissionRoute permission="platform.triage.view"><Operations /></PermissionRoute>} />
            <Route path="/management/operations/encounter/:id" element={<PermissionRoute permission="platform.triage.view"><Operations /></PermissionRoute>} />
            {/* Legacy Case Triage paths → Operations (bookmarks / deep links) */}
            <Route path="/management/triage/*" element={<Navigate to="/management/operations" replace />} />
            <Route path="/management/ai-costs" element={<PermissionRoute permission="platform.ai_analytics.view"><AICostDashboard /></PermissionRoute>} />
            <Route path="/management/external-integrations" element={<PermissionRoute permission="platform.external_integrations.manage"><ExternalIntegrationsDashboard /></PermissionRoute>} />
            <Route path="/management/coverage-audit" element={<PermissionRoute permission="platform.coverage_audit.view"><CoverageAudit /></PermissionRoute>} />
            <Route path="/management/coverage-audit/stages" element={<PermissionRoute permission="platform.coverage_audit.view"><CoverageAuditStageDashboard /></PermissionRoute>} />
            <Route path="/management/coverage-audit/model-inspector" element={<PermissionRoute permission="platform.coverage_audit.view"><CoverageAuditModelInspector /></PermissionRoute>} />
            <Route path="/management/coverage-audit/encounter/:id" element={<PermissionRoute permission="platform.coverage_audit.view"><CoverageAuditEncounterDetail /></PermissionRoute>} />
            {/* The plan permission, not platform.subscriptions.manage: that one also guards the
                platform-wide revenue dashboard below. The page shows its cross-tenant tabs only to
                callers who hold the wider grant. */}
            <Route path="/management/subscriptions" element={<PermissionRoute permission="platform.plans.view"><SubscriptionPlansPage /></PermissionRoute>} />
            <Route path="/management/billing" element={<PermissionRoute permission="platform.subscriptions.manage"><PlatformBillingPage /></PermissionRoute>} />
            <Route path="/management/compliance" element={<PermissionRoute permission="platform.system.configure"><ComplianceDashboard /></PermissionRoute>} />
          </Route>

          {/* Enterprise Portal - Amber Theme (Enterprise Admin/User Only) */}
          <Route element={<EnterpriseLayout />}>
            <Route path="/enterprise" element={<EnterpriseRoute><EnterpriseDashboard /></EnterpriseRoute>} />
            <Route path="/enterprise/partners" element={<EnterpriseRoute><EnterpriseChannelPartners /></EnterpriseRoute>} />
            <Route path="/enterprise/organizations" element={<EnterpriseRoute><EnterpriseDashboard /></EnterpriseRoute>} />
            <Route path="/enterprise/users" element={<EnterpriseRoute><EnterpriseDashboard /></EnterpriseRoute>} />
            {/* The same page the console and the partner portal mount. It rendered the dashboard
                placeholder before, so an enterprise caller could not reach a usage report at all. */}
            <Route path="/enterprise/usage" element={<EnterpriseRoute><ConsoleUsageReport /></EnterpriseRoute>} />
            <Route path="/enterprise/settings" element={<EnterpriseRoute><Profile /></EnterpriseRoute>} />
          </Route>

          {/* Partner Portal - Violet Theme (Channel Partner Admin/User Only) */}
          <Route element={<PartnerLayout />}>
            {/* Organizations is the portal's landing page; the portal has no dashboard of
                its own. The old /partner/organizations path is kept as a redirect so
                existing links and bookmarks still resolve. */}
            <Route path="/partner" element={<PartnerRoute><PartnerOrganizations /></PartnerRoute>} />
            <Route path="/partner/organizations" element={<Navigate to="/partner" replace />} />
            <Route path="/partner/users" element={<PartnerRoute><PartnerUsers /></PartnerRoute>} />
            {/* Same component the console mounts at /management/usage, so the read-only partner
                roles and the partner admin cannot see two surfaces that drift. */}
            <Route path="/partner/usage" element={<PartnerRoute><ConsoleUsageReport /></PartnerRoute>} />
            <Route path="/partner/settings" element={<PartnerRoute><Profile /></PartnerRoute>} />
          </Route>

          {/* Clinical Workspace - Blue Theme (Not accessible by superadmin) */}
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<ClinicalRoute><Dashboard /></ClinicalRoute>} />
            <Route path="/encounters" element={<ClinicalRoute><EncounterList /></ClinicalRoute>} />
            <Route path="/appointments" element={<ClinicalRoute><Appointments /></ClinicalRoute>} />
            <Route path="/appointments/new" element={<ClinicalRoute><CreateAppointment /></ClinicalRoute>} />
            <Route path="/appointments/:id" element={<ClinicalRoute><AppointmentDetails /></ClinicalRoute>} />
            <Route path="/patients" element={<ClinicalRoute><Patients /></ClinicalRoute>} />
            <Route path="/patients/new" element={<ClinicalRoute><CreatePatient /></ClinicalRoute>} />
            <Route path="/patients/:id" element={<ClinicalRoute><PatientCockpit /></ClinicalRoute>} />
            <Route path="/patients/:id/edit" element={<ClinicalRoute><EditPatient /></ClinicalRoute>} />
            {/* Recording routes - handle both encounter (query param) and appointment (path param) modes */}
            <Route path="/recording" element={<ClinicalRoute><Recording /></ClinicalRoute>} />
            <Route path="/recording/:appointmentId" element={<ClinicalRoute><Recording /></ClinicalRoute>} />
            <Route path="/templates" element={<ClinicalRoute><Templates /></ClinicalRoute>} />
            <Route path="/settings" element={<Settings />} /> {/* Settings accessible to all for profile management */}
            <Route path="/profile" element={<Profile />} /> {/* Profile accessible to all roles */}
            <Route path="/organization/usage" element={<OrganizationUsageDashboard />} /> {/* Org admins can view usage */}
            <Route path="/my-usage" element={<ClinicalRoute><MyUsagePage /></ClinicalRoute>} /> {/* Personal usage for operators/staff */}

            {/* Clinical Workflow Routes */}
            <Route path="/case/:appointmentId/validation" element={<ClinicalRoute><ClinicalValidation /></ClinicalRoute>} />
            <Route path="/encounter/:encounterId/validation" element={<ClinicalRoute><ClinicalValidation /></ClinicalRoute>} />
            {/* Smart encounter redirect - routes to recording or validation based on status */}
            <Route path="/encounter/:encounterId" element={<ClinicalRoute><EncounterRedirect /></ClinicalRoute>} />
          </Route>
        </Route>

        {/* Catch all - redirect based on auth status and role */}
        <Route
          path="*"
          element={user ? <RoleBasedHome /> : <Navigate to="/" replace />}
        />
      </Routes>


      {/* Global recording recovery handler - shows modal when interrupted recordings detected */}
      <RecordingRecoveryHandler />

      {/* Global upload job controller - surfaces upload/transcription progress across routes */}
      <UploadJobController />
    </>
  );
}

export default App;